import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Building, Hospital, Route, AlertCircle, Scale, ArrowRight, BookOpen } from "lucide-react";
import SEO from "../components/SEO";
import LeadForm from "../components/forms/LeadForm";
import CTASection from "../components/sections/CTASection";
import { getCity } from "../lib/api";
import { TOP_ACCIDENT_HUBS, CORE_PA_GUIDES } from "../lib/internalLinks";

export default function PennsylvaniaCityDetail() {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getCity(slug).then(setCity).catch(() => setCity(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-32 text-center text-charcoal-soft">Loading...</div>;
  if (!city) return (
    <div className="py-32 text-center">
      <h1 className="text-3xl font-display font-bold text-forest mb-3">City page not found</h1>
      <Link to="/pennsylvania-cities" className="btn-primary">Back to all cities</Link>
    </div>
  );

  return (
    <>
      <SEO title={city.seo_title} description={city.seo_description} />

      <section className="pt-12 md:pt-20 pb-12 md:pb-16 px-6 bg-bone">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <Link to="/pennsylvania-cities" className="inline-flex items-center gap-2 text-sm font-semibold text-charcoal-soft hover:text-terracotta mb-6">
              ← All Pennsylvania cities
            </Link>
            <div className="overline mb-3 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> {city.county} County, Pennsylvania
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold text-forest tracking-tight mb-5 text-balance">
              {city.city_name} accident help
            </h1>
            <p className="text-lg md:text-2xl text-charcoal-soft leading-relaxed">{city.intro}</p>
            <div className="mt-6 inline-flex items-center gap-3 text-sm text-charcoal-soft">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-forest/5 rounded-full">
                <Building className="w-3.5 h-3.5" /> Pop. {city.population}
              </span>
            </div>
          </div>

          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <LeadForm compact sourcePage={`city:${slug}`} />
          </div>
        </div>
      </section>

      <section className="px-6 py-12 md:py-16 bg-bone-alt">
        <div className="max-w-3xl mx-auto">
          <div className="overline mb-3">{city.city_name} Overview</div>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-forest tracking-tight mb-5">Accidents in {city.city_name}</h2>
          <p className="text-base md:text-lg text-charcoal-soft leading-relaxed">{city.overview}</p>
        </div>
      </section>

      <section className="px-6 py-12 md:py-16">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
          {city.highways?.length > 0 && (
            <Box icon={Route} title="Major highways & interstates" items={city.highways} />
          )}
          {city.hospitals?.length > 0 && (
            <Box icon={Hospital} title="Local hospitals" items={city.hospitals} />
          )}
          {city.landmarks?.length > 0 && (
            <Box icon={Building} title="Landmarks & areas" items={city.landmarks} />
          )}
          {city.accident_stats && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-terracotta" />
                <div className="overline">{city.city_name} accident stats</div>
              </div>
              <p className="text-base text-charcoal-soft leading-relaxed">{city.accident_stats}</p>
            </div>
          )}
        </div>
      </section>

      {(city.local_info || city.legal_notes) && (
        <section className="px-6 py-12 md:py-16 bg-forest text-bone">
          <div className="max-w-3xl mx-auto space-y-10">
            {city.local_info && (
              <div>
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta mb-3">Local context</div>
                <p className="text-lg text-bone/80 leading-relaxed">{city.local_info}</p>
              </div>
            )}
            {city.legal_notes && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Scale className="w-4 h-4 text-terracotta" />
                  <div className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta">Legal venue notes</div>
                </div>
                <p className="text-lg text-bone/80 leading-relaxed">{city.legal_notes}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Curated internal links: accident types + PA legal guides */}
      <section className="px-6 py-12 md:py-16" data-testid="city-related-section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">
          <div>
            <div className="overline mb-3">Explore by accident type</div>
            <h2 className="text-2xl md:text-3xl font-display font-bold text-forest tracking-tight mb-6">
              Common {city.city_name} accident claims
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {TOP_ACCIDENT_HUBS.map((h) => (
                <Link
                  key={h.slug}
                  to={`/accident-types/${h.slug}`}
                  className="group flex items-center justify-between gap-2 bg-bone-alt hover:bg-forest hover:text-bone border border-black/5 rounded-xl px-4 py-3 transition-all"
                  data-testid={`city-hub-${h.slug}`}
                >
                  <span className="font-display font-bold text-sm md:text-base text-forest group-hover:text-bone">{h.label}</span>
                  <ArrowRight className="w-4 h-4 shrink-0 opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </Link>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-4 h-4 text-terracotta" />
              <div className="overline">Pennsylvania legal guides</div>
            </div>
            <h2 className="text-2xl md:text-3xl font-display font-bold text-forest tracking-tight mb-6">
              Know your rights before you file
            </h2>
            <ul className="space-y-2">
              {CORE_PA_GUIDES.map((g) => (
                <li key={g.slug}>
                  <Link
                    to={`/blog/${g.slug}`}
                    className="group flex items-center gap-2 text-base text-charcoal-soft hover:text-terracotta transition-colors py-1.5"
                    data-testid={`city-guide-${g.slug}`}
                  >
                    <ArrowRight className="w-4 h-4 text-terracotta shrink-0 group-hover:translate-x-0.5 transition-transform" />
                    <span className="border-b border-transparent group-hover:border-terracotta">{g.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow={`${city.city_name}, Pennsylvania`}
        title={`Need help after a ${city.city_name} accident?`}
        description={`Connect with a local ${city.county} County attorney within minutes. Free, confidential, no obligation.`}
      />
    </>
  );
}

function Box({ icon: Icon, title, items }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5 text-terracotta" />
        <div className="overline">{title}</div>
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="text-base text-charcoal-soft">{it}</li>
        ))}
      </ul>
    </div>
  );
}
